// src/utils/fetch-utils.ts
var DEFAULT_TIMEOUT_MS = 3e4;
var BASE_URL;
function initApi(config) {
  BASE_URL = config.baseUrl;
}
function getBaseUrl() {
  return BASE_URL;
}
function getApiUrl(endpoint) {
  const version = /^\/v\d/.test(endpoint) ? "" : "v1/";
  return `${BASE_URL}/${version}${endpoint.replace(/^\/+/, "")}`;
}
var impersonationSessionIdProvider = null;
function setImpersonationSessionIdProvider(provider) {
  impersonationSessionIdProvider = provider;
}
function getManualFetchHeaders() {
  return getImpersonationHeader();
}
function getImpersonationHeader() {
  const id = impersonationSessionIdProvider?.() ?? null;
  return id ? { "X-Impersonate-Session": id } : {};
}
async function fetchApi(endpoint, method, data, options = {}) {
  const { headers = {}, params = {}, withCredentials = true, timeoutMs = DEFAULT_TIMEOUT_MS } = options;
  const version = /^\/v\d/.test(endpoint) ? "" : "v1/";
  const url = new globalThis.URL(`${BASE_URL}/${version}${endpoint.replace(/^\/+/, "")}`);
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      url.searchParams.append(key, value);
    }
  });
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  const fetchOptions = {
    method,
    headers: {
      "Content-Type": "application/json",
      ...getImpersonationHeader(),
      ...headers
    },
    credentials: withCredentials ? "include" : "omit",
    signal: controller.signal
  };
  if (method !== "GET" && data !== void 0) {
    fetchOptions.body = JSON.stringify(data);
  }
  const response = await fetch(url.toString(), fetchOptions).finally(
    () => clearTimeout(timeoutId)
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    let errorMessage = errorData?.message;
    let errorIssues = errorData?.issues;
    if (errorData?.error?.name === "ZodError" && Array.isArray(errorData.error.issues)) {
      const zodError = errorData.error;
      errorIssues = zodError.issues;
      errorMessage = zodError.issues.map((i) => i.message).join("\n");
    }
    const error = new Error(
      errorMessage || `API error: ${response.status} ${response.statusText}`
    );
    Object.assign(error, {
      status: response.status,
      statusText: response.statusText,
      message: errorMessage,
      data: errorData,
      issues: errorIssues
    });
    throw error;
  }
  const contentType = response.headers.get("content-type");
  if (contentType?.includes("application/json")) {
    return response.json();
  }
  return {};
}
var api = {
  /**
   * Make a GET request
   */
  get: (endpoint, options) => fetchApi(endpoint, "GET", void 0, options),
  /**
   * Make a POST request
   */
  post: (endpoint, data, options) => fetchApi(endpoint, "POST", data, options),
  /**
   * Make a PUT request
   */
  put: (endpoint, data, options) => fetchApi(endpoint, "PUT", data, options),
  /**
   * Make a DELETE request
   */
  delete: (endpoint, options) => fetchApi(endpoint, "DELETE", void 0, options),
  /**
   * Make a PATCH request
   */
  patch: (endpoint, data, options) => fetchApi(endpoint, "PATCH", data, options),
  /**
   * Make a POST request with FormData (for file uploads)
   */
  postFormData: async (endpoint, data, options = {}) => {
    const { headers = {}, params = {}, withCredentials = true, timeoutMs = DEFAULT_TIMEOUT_MS } = options;
    const url = new globalThis.URL(
      `${BASE_URL}/v1/${endpoint.replace(/^\/+/, "")}`
    );
    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        url.searchParams.append(key, value);
      }
    });
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    const fetchOptions = {
      method: "POST",
      headers: {
        ...getImpersonationHeader(),
        ...headers
      },
      credentials: withCredentials ? "include" : "omit",
      body: data,
      signal: controller.signal
    };
    const response = await fetch(url.toString(), fetchOptions).finally(
      () => clearTimeout(timeoutId)
    );
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      let errorMessage = errorData?.message;
      let errorIssues = errorData?.issues;
      if (errorData?.error?.name === "ZodError" && Array.isArray(errorData.error.issues)) {
        const zodError = errorData.error;
        errorIssues = zodError.issues;
        errorMessage = zodError.issues.map((i) => i.message).join("\n");
      }
      const error = new Error(
        errorMessage || `API error: ${response.status} ${response.statusText}`
      );
      Object.assign(error, {
        status: response.status,
        statusText: response.statusText,
        message: errorMessage,
        data: errorData,
        issues: errorIssues
      });
      throw error;
    }
    const contentType = response.headers.get("content-type");
    if (contentType?.includes("application/json")) {
      return response.json();
    }
    return {};
  }
};

// src/addresses/addresses.api.ts
var AddressesApi = class {
  static async search(q, opts) {
    const params = new URLSearchParams({ q });
    if (opts?.limit !== void 0) params.set("limit", String(opts.limit));
    if (opts?.language) params.set("language", opts.language);
    return api.get(`/addresses/search?${params.toString()}`);
  }
};

// src/drivers/drivers.api.ts
import { DRIVER_DOCUMENT_FIELD_MAP } from "@repo/shared";

// src/utils/form-data.ts
function buildDocumentFormData(data, documentFieldNames) {
  const fileKeys = new Set(documentFieldNames);
  const expiryKeys = new Set([...fileKeys].map((k) => `${k}Expiry`));
  const formData = new FormData();
  for (const [key, value] of Object.entries(data)) {
    if (value === void 0 || value === null) continue;
    if (fileKeys.has(key)) {
      if (value instanceof File) {
        formData.append(key, value);
      }
      continue;
    }
    if (expiryKeys.has(key)) {
      if (value instanceof Date) {
        formData.append(key, value.toISOString());
      }
      continue;
    }
    if (value instanceof Date) {
      formData.append(key, value.toISOString());
      continue;
    }
    formData.append(key, String(value));
  }
  return formData;
}

// src/drivers/drivers.api.ts
var DRIVER_DOCUMENT_FIELD_NAMES = Object.keys(DRIVER_DOCUMENT_FIELD_MAP);
var DriversApi = class {
  static async createDriver(data) {
    const formData = buildDocumentFormData(
      data,
      DRIVER_DOCUMENT_FIELD_NAMES
    );
    return api.postFormData("/drivers", formData);
  }
  static async getDriver(id) {
    return await api.get(`/drivers/${id}`);
  }
  static async getDrivers() {
    return await api.get(
      "/drivers"
    );
  }
  static async getDriversPaginated(params) {
    return await api.get("/drivers/paginated", {
      params: {
        page: String(params.page),
        pageSize: String(params.pageSize),
        ...params.search && { search: params.search },
        ...params.status && { status: params.status }
      }
    });
  }
  static async updateDriver(id, data) {
    return await api.put(`/drivers/${id}`, data);
  }
  static async deleteDriver(id) {
    return await api.delete(`/drivers/${id}`);
  }
};

// src/devices/devices.api.ts
var DevicesApi = class {
  static async createDevice(data) {
    return await api.post("/devices", data);
  }
  static async getDevices() {
    return await api.get("/devices");
  }
  static async getDevice(id) {
    return await api.get(`/devices/${id}`);
  }
  static async updateDevice(id, data) {
    return await api.patch(`/devices/${id}`, data);
  }
  static async linkDeviceByVin(id, data) {
    return await api.patch(`/devices/${id}/link-by-vin`, data);
  }
};

// src/telemetry/telemetry.api.ts
var TelemetryApi = class {
  static async getLocations() {
    return await api.get("/telemetry/locations");
  }
  static async getSnapshotByVehicleVin(vehicleVin) {
    return await api.get(
      `/telemetry/snapshot/${vehicleVin}`
    );
  }
  static async getLatestByVehicleId(vehicleId) {
    return await api.get(
      `/telemetry/vehicles/${vehicleId}/latest`
    );
  }
  static async getVehicleStats(vehicleId, from, to) {
    return await api.get(
      `/telemetry/vehicles/${vehicleId}/stats?from=${from.toISOString()}&to=${to.toISOString()}`
    );
  }
  static async getVehicleHistory(vehicleId, from, to) {
    return await api.get(
      `/telemetry/vehicles/${vehicleId}/history?from=${from.toISOString()}&to=${to.toISOString()}`
    );
  }
  static async getVehicleTrack(vehicleId, from, to) {
    return await api.get(
      `/telemetry/vehicles/${vehicleId}/track?from=${from.toISOString()}&to=${to.toISOString()}`
    );
  }
  static async getVehicleTempSeries(vehicleId, from, to) {
    return await api.get(
      `/telemetry/vehicles/${vehicleId}/temp-series?from=${from.toISOString()}&to=${to.toISOString()}`
    );
  }
};

// src/indicators/indicators.api.ts
var IndicatorsApi = class {
  static async getActive(vehicleId) {
    return await api.get(
      `/indicators/vehicles/${vehicleId}/active`
    );
  }
  /** Org-wide fleet health: faulted vehicles only, worst-first. */
  static async getFleetHealth() {
    return await api.get("/indicators/health");
  }
};

// src/vehicles/vehicles.api.ts
import {
  TRAILER_DOCUMENT_FIELD_MAP,
  TRUCK_DOCUMENT_FIELD_MAP
} from "@repo/shared";
var TRUCK_DOCUMENT_FIELD_NAMES = Object.keys(TRUCK_DOCUMENT_FIELD_MAP);
var TRAILER_DOCUMENT_FIELD_NAMES = Object.keys(TRAILER_DOCUMENT_FIELD_MAP);
var vehiclesApi = {
  async getVehicles(params) {
    return api.get("/vehicles", {
      params: params?.hasDevice ? { hasDevice: "true" } : void 0
    });
  },
  async getVehiclesPaginated(params) {
    return api.get("/vehicles/paginated", {
      params: {
        page: String(params.page),
        pageSize: String(params.pageSize),
        ...params.type && { type: params.type },
        ...params.excludeType && { excludeType: params.excludeType },
        ...params.hasDevice && { hasDevice: "true" },
        ...params.search && { search: params.search }
      }
    });
  },
  async getTrucks() {
    return api.get("/vehicles/trucks");
  },
  async getTrucksPaginated(params) {
    return api.get("/vehicles/trucks/paginated", {
      params: {
        page: String(params.page),
        pageSize: String(params.pageSize),
        ...params.search && { search: params.search }
      }
    });
  },
  async getTruckByVehicleId(vehicleId) {
    return api.get(`/vehicles/trucks/${vehicleId}`);
  },
  async addTruck(data) {
    const formData = buildDocumentFormData(
      data,
      TRUCK_DOCUMENT_FIELD_NAMES
    );
    return api.postFormData(
      "/vehicles/trucks",
      formData
    );
  },
  async editTruck(vehicleId, data) {
    return api.patch(`/vehicles/trucks/${vehicleId}`, data);
  },
  // ─── Trailers ──────────────────────────────────────────────
  async getTrailers(params) {
    const query = {};
    if (params?.unassignedOnly) query.unassignedOnly = "true";
    if (params?.includeTrailerId)
      query.includeTrailerId = String(params.includeTrailerId);
    return api.get("/vehicles/trailers", {
      params: Object.keys(query).length ? query : void 0
    });
  },
  async getTrailersPaginated(params) {
    return api.get("/vehicles/trailers/paginated", {
      params: {
        page: String(params.page),
        pageSize: String(params.pageSize),
        ...params.search && { search: params.search }
      }
    });
  },
  async addTrailer(data) {
    const formData = buildDocumentFormData(
      data,
      TRAILER_DOCUMENT_FIELD_NAMES
    );
    return api.postFormData(
      "/vehicles/trailers",
      formData
    );
  },
  async getTrailerByVehicleId(vehicleId) {
    return api.get(`/vehicles/trailers/${vehicleId}`);
  },
  async editTrailer(vehicleId, data) {
    return api.patch(`/vehicles/trailers/${vehicleId}`, data);
  },
  async assignTrailerToTruck(trailerId, truckId) {
    return api.post(`/vehicles/trailers/${trailerId}/assign/${truckId}`);
  },
  async unassignTrailerFromTruck(trailerId, truckId) {
    return api.put(`/vehicles/trailers/${trailerId}/unassign/${truckId}`);
  },
  // ─── Vehicle Assignment (Driver ↔ Vehicle) ────────────────
  async assignVehicle(vehicleId, driverId) {
    return api.post(`/vehicles/${vehicleId}/assign/${driverId}`);
  },
  async unassignVehicle(vehicleId, driverId) {
    return api.put(`/vehicles/${vehicleId}/unassign/${driverId}`);
  },
  // ─── Vehicle Details Modal ────────────────────────────────
  async getVehicleDetails(vehicleId) {
    return api.get(`/vehicles/${vehicleId}/details`);
  },
  async getVehicleTelemetry(vehicleId) {
    return api.get(
      `/vehicles/${vehicleId}/telemetry`
    );
  },
  async getVehicleDriverHistory(vehicleId, params) {
    return api.get(
      `/vehicles/${vehicleId}/activity/drivers`,
      {
        params: {
          page: String(params.page),
          pageSize: String(params.pageSize)
        }
      }
    );
  },
  async getVehicleTrailerHistory(vehicleId, params) {
    return api.get(
      `/vehicles/${vehicleId}/activity/trailers`,
      {
        params: {
          page: String(params.page),
          pageSize: String(params.pageSize)
        }
      }
    );
  },
  // ─── Generic ──────────────────────────────────────────────
  async deleteVehicle(vehicleId) {
    return api.delete(`/vehicles/${vehicleId}`);
  }
};

// src/auth/auth.ts
var AuthApi = class {
  static async login(data) {
    return await api.post("/auth/login", data);
  }
  static async logout() {
    return await api.post("/auth/logout");
  }
  static async me() {
    return await api.get("/users/me");
  }
  static async forgotPassword(data) {
    return await api.post(
      "/auth/forgot-password",
      data
    );
  }
  static async resetPassword(data) {
    return await api.post("/auth/reset-password", data);
  }
};

// src/share/share.api.ts
var ShareApi = class {
  static async createShare(vehicleId, force = false, expiresInHours) {
    return await api.post("/share", {
      vehicleId,
      force,
      expiresInHours
    });
  }
  // FM-227: create a public route-tracking share link.
  static async createRouteShare(routeId, force = false, expiresInHours) {
    return await api.post("/share/route", {
      routeId,
      force,
      expiresInHours
    });
  }
  static async getShareData(token) {
    return await api.get(`/share/${token}`);
  }
};

// src/tachograph/tachograph.api.ts
function buildParams(params) {
  const result = {
    pageNumber: String(params.pageNumber),
    pageSize: String(params.pageSize)
  };
  if (params.from) result.from = params.from;
  if (params.to) result.to = params.to;
  if (params.contains) result.contains = params.contains;
  if (params.orderBy) result.orderBy = params.orderBy;
  if (params.descending !== void 0)
    result.descending = String(params.descending);
  if (params.vehicleId !== void 0)
    result.vehicleId = String(params.vehicleId);
  if (params.driverId !== void 0) result.driverId = String(params.driverId);
  return result;
}
var TachographApi = class {
  static async getVehicleFiles(params) {
    return api.get(
      "/tachograph/vehicle-files",
      { params: buildParams(params) }
    );
  }
  static async getDriverFiles(params) {
    return api.get(
      "/tachograph/driver-files",
      { params: buildParams(params) }
    );
  }
  static getVehicleFilesDownloadUrl(ids) {
    const params = new URLSearchParams();
    for (const id of ids) {
      params.append("ids", id);
    }
    return `/v1/tachograph/vehicle-files/download?${params.toString()}`;
  }
  static getDriverFilesDownloadUrl(ids) {
    const params = new URLSearchParams();
    for (const id of ids) {
      params.append("ids", id);
    }
    return `/v1/tachograph/driver-files/download?${params.toString()}`;
  }
  static getReportDownloadUrl(type, id, opts) {
    const locale = encodeURIComponent(opts?.locale ?? "en");
    return `/v1/tachograph/${type}-files/${encodeURIComponent(id)}/report?locale=${locale}`;
  }
  static async getVehicleActivities(vehicleId, from, to) {
    return api.get(
      `/tachograph/vehicles/${vehicleId}/activities?from=${from.toISOString()}&to=${to.toISOString()}`
    );
  }
  static async getDriverActivities(driverId, from, to) {
    return api.get(
      `/tachograph/drivers/${driverId}/activities?from=${from.toISOString()}&to=${to.toISOString()}`
    );
  }
  static async getDriverWeeklySummaries(vehicleId, includeCoDriver = false) {
    return api.get(
      `/tachograph/vehicles/${vehicleId}/driver-weekly-summaries${includeCoDriver ? "?includeCoDriver=true" : ""}`
    );
  }
  // Driver-keyed remaining HOS for route feasibility preview (FM-237).
  // Resolves null when the driver has no tachograph card data.
  static async getDriverRemainingHos(driverId) {
    return api.get(
      `/tachograph/drivers/${driverId}/remaining-hos`
    );
  }
  static async getParsedVehicleFile(id) {
    return api.get(
      `/tachograph/vehicle-files/${encodeURIComponent(id)}/parsed`
    );
  }
  static async getParsedDriverFile(id) {
    return api.get(
      `/tachograph/driver-files/${encodeURIComponent(id)}/parsed`
    );
  }
};

// src/tachograph/tacho-schedule.api.ts
var BASE = "/tachograph/schedules";
var TachoScheduleApi = class {
  static async list(type) {
    return api.get(`${BASE}/${type}`);
  }
  static async assignments(type) {
    return api.get(`${BASE}/${type}/assignments`);
  }
  static async detail(type, id) {
    return api.get(
      `${BASE}/${type}/detail/${encodeURIComponent(id)}`
    );
  }
  static async createDriver(data) {
    return api.post(`${BASE}/driver`, data);
  }
  static async createVehicle(data) {
    return api.post(`${BASE}/vehicle`, data);
  }
  static async editDriver(data) {
    return api.put(`${BASE}/driver`, data);
  }
  static async editVehicle(data) {
    return api.put(`${BASE}/vehicle`, data);
  }
  static async apply(type, data) {
    return api.put(`${BASE}/${type}/apply`, data);
  }
  static async applyToAll(type, data) {
    return api.put(`${BASE}/${type}/apply-to-all`, data);
  }
  /** Fire a one-time Quick download for the selected vehicles. */
  static async quickVehicle(data) {
    return api.post(`${BASE}/quick/vehicle`, data);
  }
  /** Fire a one-time Quick download for the selected drivers. */
  static async quickDriver(data) {
    return api.post(`${BASE}/quick/driver`, data);
  }
  static async remove(type, id) {
    return api.delete(`${BASE}/${type}/${encodeURIComponent(id)}`);
  }
  static async removeAssignment(type, entityId, scheduleId) {
    return api.delete(
      `${BASE}/${type}/${entityId}/schedule/${encodeURIComponent(scheduleId)}`
    );
  }
  /** Cancel all pending Quick (one-time) downloads for one entity. */
  static async removeQuick(type, entityId) {
    return api.delete(`${BASE}/${type}/${entityId}/quick`);
  }
};

// src/tachograph/tacho-roster.api.ts
var BASE2 = "/tachograph/roster";
function serializeFilters(filters) {
  const params = {};
  if (filters.search) params.search = filters.search;
  if (filters.downloadStatuses?.length) {
    params.downloadStatuses = filters.downloadStatuses.join(",");
  }
  if (filters.deviceStatuses?.length) {
    params.deviceStatuses = filters.deviceStatuses.join(",");
  }
  if (filters.statuses?.length) params.statuses = filters.statuses.join(",");
  if (filters.noDeviceImei) params.noDeviceImei = "true";
  if (filters.scheduleIds?.length) {
    params.scheduleIds = filters.scheduleIds.join(",");
  }
  if (filters.noSchedulesAssigned) params.noSchedulesAssigned = "true";
  return params;
}
var TachoRosterApi = class {
  static async list(type, params) {
    const { page, pageSize, ...filters } = params;
    return api.get(`${BASE2}/${type}`, {
      params: {
        ...serializeFilters(filters),
        page: String(page),
        pageSize: String(pageSize)
      }
    });
  }
  // The ids endpoint whitelists filter params only — page/pageSize would be
  // rejected with a 400 (forbidNonWhitelisted), so they are never sent here.
  static async ids(type, filters) {
    return api.get(`${BASE2}/${type}/ids`, {
      params: serializeFilters(filters)
    });
  }
};

// src/compliance/compliance.api.ts
var ComplianceApi = class {
  static async getInfringements(params) {
    const searchParams = {};
    if (params) {
      for (const [key, value] of Object.entries(params)) {
        if (value != null) searchParams[key] = String(value);
      }
    }
    const query = new URLSearchParams(searchParams).toString();
    return api.get(
      `/compliance/infringements${query ? `?${query}` : ""}`
    );
  }
  static async getInfringementStats() {
    return api.get(
      "/compliance/infringements/stats"
    );
  }
  static getReportDownloadUrl(params) {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value != null) searchParams.set(key, String(value));
    }
    return getApiUrl(`/compliance/reports/generate?${searchParams.toString()}`);
  }
  static async acknowledgeInfringement(id, notes) {
    return api.patch(
      `/compliance/infringements/${id}/acknowledge`,
      notes != null ? { notes } : {}
    );
  }
};

// src/analytics/analytics.api.ts
var AnalyticsApi = class {
  static async getFleetAnalytics(period) {
    return await api.get(
      `/analytics/fleet?period=${period}`
    );
  }
  static async getUtilization() {
    return await api.get("/analytics/utilization");
  }
};

// src/dashboard/dashboard.api.ts
var DashboardApi = class {
  static async getActionCenter() {
    return await api.get("/dashboard/alerts");
  }
};

// src/fuel-anomaly/fuel-anomaly.api.ts
var FuelAnomalyApi = class {
  static async getEvents(params) {
    const searchParams = {};
    if (params) {
      for (const [key, value] of Object.entries(params)) {
        if (value != null) searchParams[key] = String(value);
      }
    }
    const query = new URLSearchParams(searchParams).toString();
    return api.get(
      `/fuel-anomaly${query ? `?${query}` : ""}`
    );
  }
  static async acknowledge(id, notes) {
    return api.patch(
      `/fuel-anomaly/${id}/acknowledge`,
      notes != null ? { notes } : {}
    );
  }
};

// src/documents/documents.api.ts
var DocumentsApi = class {
  static async getAll(filters) {
    const params = {};
    if (filters?.entityType) params.entityType = filters.entityType;
    if (filters?.documentType) params.documentType = filters.documentType;
    if (filters?.status) params.status = filters.status;
    return api.get("/documents", { params });
  }
  static async getAllPaginated(params) {
    return api.get(
      "/documents/paginated",
      {
        params: {
          page: String(params.page),
          pageSize: String(params.pageSize),
          ...params.search && { search: params.search },
          ...params.expiry && { expiry: params.expiry },
          ...params.entityType && { entityType: params.entityType }
        }
      }
    );
  }
  static async getStats() {
    return api.get("/documents/stats");
  }
  static async getByEntity(entityType, entityId) {
    return api.get(
      `/documents/entity/${entityType}/${entityId}`
    );
  }
  static async uploadDocument(entityType, entityId, file, documentType, expiryDate, tags) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("documentType", documentType);
    if (expiryDate) {
      formData.append("expiryDate", expiryDate.toISOString());
    }
    if (tags && tags.length > 0) {
      formData.append("tags", JSON.stringify(tags));
    }
    return api.postFormData(
      `/documents/entity/${entityType}/${entityId}`,
      formData
    );
  }
  static getDownloadUrl(id) {
    return getApiUrl(`/documents/${id}/download`);
  }
  static async deleteDocument(id) {
    return api.delete(`/documents/${id}`);
  }
};

// src/routes/routes.api.ts
var RoutesApi = class {
  static async getAll() {
    return api.get("/routes");
  }
  static async getById(id) {
    return api.get(`/routes/${id}`);
  }
  static async createRoute(data) {
    return api.post("/routes", data);
  }
  static async updateRoute(id, data) {
    return api.patch(`/routes/${id}`, data);
  }
  static async duplicateRoute(id) {
    return api.post(`/routes/${id}/duplicate`);
  }
  // FM-232: plan HOS rests at secure-parking POIs (preview, before save).
  static async planRests(data) {
    return api.post("/routes/plan-rests", data);
  }
  static async toggleFlags(id, flags) {
    return api.patch(`/routes/${id}/flags`, flags);
  }
  static async deleteRoute(id) {
    return api.delete(`/routes/${id}`);
  }
  // FM-227: manually check off a stop (arrived/completed). Returns the route.
  static async checkOffStop(id, stopId, status) {
    return api.post(`/routes/${id}/stops/${stopId}/check-off`, {
      status
    });
  }
  // FM-227: refresh the live ETA with current HERE traffic (internal only).
  static async refreshTracking(id) {
    return api.post(`/routes/${id}/tracking/refresh`);
  }
  // Driver sheet (PDF) is a binary download — return the URL so the caller can
  // fetch it as a blob (mirrors ComplianceApi.getReportDownloadUrl).
  static getSheetDownloadUrl(id, opts) {
    const locale = opts?.locale ?? "en";
    return getApiUrl(
      `/routes/${id}/sheet?locale=${encodeURIComponent(locale)}`
    );
  }
};

// src/feedback/feedback.api.ts
var FeedbackApi = class {
  static async createFeedback(data) {
    return api.post("/feedback", data);
  }
  static async getFeedbacksPaginated(params) {
    return api.get("/admin/feedback", {
      params: {
        page: String(params.page),
        pageSize: String(params.pageSize),
        ...params.search && { search: params.search },
        ...params.status && { status: params.status },
        ...params.category && { category: params.category }
      }
    });
  }
  static async updateFeedbackStatus(id, status) {
    return api.patch(`/admin/feedback/${id}`, { status });
  }
};

// src/organizations/organizations.api.ts
var OrganizationsApi = class {
  static async updateOrganizationSettings(data) {
    return await api.patch(
      "/organizations/settings",
      data
    );
  }
};

// src/admin/organizations/admin-organizations.api.ts
var adminOrganizationsApi = {
  async list(params) {
    const query = {
      page: String(params.page),
      pageSize: String(params.pageSize)
    };
    if (params.search) query.search = params.search;
    if (params.status) query.status = params.status;
    if (params.sort) query.sort = params.sort;
    if (params.sortDir) query.sortDir = params.sortDir;
    return api.get("/admin/organizations", { params: query });
  },
  async getById(id) {
    return api.get(`/admin/organizations/${id}`);
  },
  async create(data) {
    return api.post("/admin/organizations", data);
  },
  async update(id, data) {
    return api.patch(`/admin/organizations/${id}`, data);
  },
  async disable(id) {
    return api.patch(`/admin/organizations/${id}/disable`);
  },
  async enable(id) {
    return api.patch(`/admin/organizations/${id}/enable`);
  },
  async updatePlan(id, plan, pilotExpiresAt) {
    return api.patch(`/admin/organizations/${id}/plan`, { plan, pilotExpiresAt });
  },
  async getRetention(id) {
    return api.get(`/admin/organizations/${id}/retention`);
  },
  async updateRetention(id, data) {
    return api.patch(`/admin/organizations/${id}/retention`, data);
  },
  async setTachoSyncKey(id, apiKey) {
    return api.patch(`/admin/organizations/${id}/tachosync-key`, { apiKey });
  },
  async getMembers(id) {
    return api.get(`/admin/organizations/${id}/members`);
  },
  async getVehicles(id) {
    return api.get(`/admin/organizations/${id}/vehicles`);
  }
};

// src/admin/users/admin-users.api.ts
var adminUsersApi = {
  async list(params) {
    const query = {
      page: String(params.page),
      pageSize: String(params.pageSize)
    };
    if (params.search) query.search = params.search;
    if (params.platformRole) query.platformRole = params.platformRole;
    if (params.status) query.status = params.status;
    if (params.sort) query.sort = params.sort;
    if (params.sortDir) query.sortDir = params.sortDir;
    return api.get("/admin/users", { params: query });
  },
  async getById(id) {
    return api.get(`/admin/users/${id}`);
  },
  async create(data) {
    return api.post("/admin/users", data);
  },
  async update(id, data) {
    return api.patch(`/admin/users/${id}`, data);
  },
  async disable(id) {
    return api.patch(`/admin/users/${id}/disable`);
  },
  async enable(id) {
    return api.patch(`/admin/users/${id}/enable`);
  },
  async resetPassword(id) {
    return api.patch(`/admin/users/${id}/reset-password`);
  }
};

// src/admin/devices/admin-devices.api.ts
var adminDevicesApi = {
  async list(params) {
    const query = {
      page: String(params.page),
      pageSize: String(params.pageSize)
    };
    if (params.search) query.search = params.search;
    if (typeof params.organizationId === "number")
      query.organizationId = String(params.organizationId);
    if (typeof params.hasVehicle === "boolean")
      query.hasVehicle = String(params.hasVehicle);
    if (params.unassigned) query.unassigned = "true";
    if (params.sort) query.sort = params.sort;
    if (params.sortDir) query.sortDir = params.sortDir;
    return api.get("/admin/devices", { params: query });
  },
  async getById(id) {
    return api.get(`/admin/devices/${id}`);
  },
  async create(data) {
    return api.post("/admin/devices", data);
  },
  async update(id, data) {
    return api.patch(`/admin/devices/${id}`, data);
  },
  async assignVehicle(id, vehicleId) {
    return api.patch(`/admin/devices/${id}/assign-vehicle`, { vehicleId });
  },
  async delete(id) {
    return api.delete(`/admin/devices/${id}`);
  },
  async getVehiclesForSelect(organizationId) {
    const query = {};
    if (typeof organizationId === "number")
      query.organizationId = String(organizationId);
    return api.get("/admin/devices/vehicles", {
      params: Object.keys(query).length ? query : void 0
    });
  }
};

// src/admin/impersonation/impersonation.api.ts
var impersonationApi = {
  async start(data) {
    return api.post("/admin/impersonate/start", data);
  },
  async stop(sessionId) {
    return api.post("/admin/impersonate/stop", { sessionId });
  },
  async getActive() {
    return api.get("/admin/impersonate/active");
  }
};
export {
  AddressesApi,
  AnalyticsApi,
  AuthApi,
  ComplianceApi,
  DashboardApi,
  DevicesApi,
  DocumentsApi,
  DriversApi,
  FeedbackApi,
  FuelAnomalyApi,
  IndicatorsApi,
  OrganizationsApi,
  RoutesApi,
  ShareApi,
  TachoRosterApi,
  TachoScheduleApi,
  TachographApi,
  TelemetryApi,
  adminDevicesApi,
  adminOrganizationsApi,
  adminUsersApi,
  api,
  getApiUrl,
  getBaseUrl,
  getManualFetchHeaders,
  impersonationApi,
  initApi,
  setImpersonationSessionIdProvider,
  vehiclesApi
};
//# sourceMappingURL=index.mjs.map